import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import chain from 'lodash';
import { rd } from '@passionware/monads';

import { selectAppId } from 'store/v1/auth/auth.selectors';
import { makeGetSlotName } from 'store/v1/slot_categories/slot_categories.selectors.js';
import { makeGetProductionFromResourceSlotId } from 'store/v1/productions/productions.selectors.js';
import { orderReducer } from 'v1/helpers/misc';
import { myRouting } from 'v5/features/core/routing';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';
import { myServices } from 'v5/services/services.connected.app.js';

import {
  PopoverSection,
  RateItem,
  EmptyGeneric,
  MiniLoader
} from 'v1/components/shared';

const RateSelectorItem = ({ rate, onClick }) => (
  <RateItem
    key={get(rate, 'id')}
    rate={rate}
    readOnly={true}
    inset={true}
    onClick={onClick}
  />
);

const RateSelectorContent = ({
  resourceId,
  assignments = [],
  showRateCards,
  closePopover,
  onSelect
}) => {
  const appId = useSelector(selectAppId);
  const rateCardsHref = myRouting.forWorkspace(appId).settings().rateCards();
  const rates = useSelector(state => state.rates);
  const rateCards = chain(orderReducer(rates))
    .orderBy(['name', 'asc'])
    .filter(r => !r.archived)
    .value();

  const resourceRd = myServices.resourceService.useItem(resourceId);
  const getSlotName = useSelector(makeGetSlotName);

  // TODO: ripped out of old RateSourceSelector - clean up
  // We do not have access to resource_slot object without getting the production on new bookings.
  const getProductionFromResourceSlotId = useMemo(
    makeGetProductionFromResourceSlotId,
    []
  );
  const selectedProductions = useSelector(state =>
    getProductionFromResourceSlotId(state, { assignments })
  );
  const getResourceSlotFromProductionList = resourceSlotId => {
    let selectedResourceSlot = {};
    selectedProductions.map(production => {
      production.resource_slots.map(slot =>
        slot.id === resourceSlotId ? (selectedResourceSlot = slot) : null
      );
    });
    return selectedResourceSlot;
  };

  const budgetedRates = assignments.reduce((acc, assignment) => {
    const slot = get(
      assignment,
      'resource_slot',
      getResourceSlotFromProductionList(assignment.resource_slot_id)
    );
    const rateAmount = get(slot, 'estimate_rate_amount');
    if (rateAmount) {
      acc.push({
        slot_id: get(slot, 'id'),
        id: get(slot, 'estimate_rate_id'),
        name: getSlotName(slot),
        amount: get(slot, 'estimate_rate_amount'),
        currency: get(slot, 'estimate_rate_currency'),
        interval: get(slot, 'estimate_rate_interval'),
        quantity: get(slot, 'estimate_rate_quantity'),
        agency_fee: get(slot, 'estimate_rate_agency_fee'),
        payroll_fee: get(slot, 'estimate_rate_payroll_fee'),
        processing_fee: get(slot, 'estimate_rate_processing_fee')
      });
    }
    return acc;
  }, []);

  const handleSelect = rate => {
    onSelect(rate);
    closePopover();
  };

  return (
    <>
      {rd
        .fullJourney(resourceRd)
        .initially(null)
        .wait(
          <PopoverSection title="Resource Rates">
            <MiniLoader isLoading />
          </PopoverSection>
        )
        .catch(renderError.withTailwindRoot)
        .map(resource =>
          resource.rates?.length > 0 ? (
            <PopoverSection title="Resource Rates">
              {resource.rates.map(rate => (
                <RateSelectorItem
                  key={rate.id}
                  rate={rate}
                  onClick={handleSelect}
                />
              ))}
            </PopoverSection>
          ) : null
        )}
      {budgetedRates.length > 0 && (
        <PopoverSection title="Budgeted Rates">
          {budgetedRates.map(rate => (
            <RateSelectorItem
              key={rate.slot_id}
              rate={rate}
              onClick={handleSelect}
            />
          ))}
        </PopoverSection>
      )}
      {showRateCards && (
        <>
          {rateCards.length > 0 ? (
            <PopoverSection title="Rate Cards">
              {rateCards.map(rate => (
                <RateSelectorItem
                  key={rate.id}
                  rate={rate}
                  onClick={handleSelect}
                />
              ))}
            </PopoverSection>
          ) : (
            <EmptyGeneric
              title="No Rate Cards"
              description="Define rate cards in settings to standardise rates for your company"
              icon="/images/icon_colour_rates.svg"
              action={{
                text: 'Go to rate settings',
                className: 'btn btn-default btn-small',
                href: rateCardsHref
              }}
            />
          )}
        </>
      )}
    </>
  );
};

RateSelectorContent.props = {
  resourceId: PropTypes.number,
  assignments: PropTypes.arrayOf(PropTypes.object), // booking.resource_slot_assignments - For budgeted rates
  showRateCards: PropTypes.bool,
  closePopover: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
};

export default RateSelectorContent;
